import { Component, Injector, Input, ChangeDetectionStrategy } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { Company } from '../../model/company';
import { tenantCommand } from '../../model/const/command';
import { CompanyFormCommandHandlerResolver } from '../../service/command/company-form-command-handler-resolver.service';
import { CompanyDocumentService } from '../../service/data/company-document.service';
import { CompanyDocumentFormComponent } from '../company-document-form/company-document-form.component';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

@Component({
    selector: 'app-company-document[company]',
    templateUrl: './company-document.component.html',
    styleUrls: ['./company-document.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyDocumentComponent extends CommandController {
  documentsColumnDefinition: ColumnDefinition[];
  documentsContextMenuDefinitions: MenuItemDefinition[];
  documentsLoaded: boolean;

  @Input() company: Company;

  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _injector: Injector,
    private _actionWindowService: ActionWindowService,
    private documentService: CompanyDocumentService,
    private _nativeWindow: NativeWindowService,
    private _userAccount: UserAccountService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CompanyFormCommandHandlerResolver
  ) {
    super(commandDispatcher, handlerResolver);

    this.AddSubscription(
      _userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditCompany)) {
            this.allowLinkCompanyDocument = true;
          }
        },
      })
    );

    this.documentsColumnDefinition = [
      this._cdf.create('DOCUMENT.CAPTION', 'caption'),
      this._cdf.createDateTime('DOCUMENT.UPLOADED_DATE', 'uploadedDate'),
      this._cdf.create('DOCUMENT.SIZE', 'size', ColumnVisibility.optional),
      this._cdf.createTranslation('DOCUMENT.DOCUMENT_TYPE', 'connectionTypeText', ColumnVisibility.default),
    ];

    this.documentsContextMenuDefinitions = [
      new MenuItemDefinition('DOWNLOAD_BTN', tenantCommand.downloadDocument),
      new MenuItemDefinition('DELETE_BTN', tenantCommand.deleteDocument),
    ];
  }

  allowLinkCompanyDocument: boolean;

  linkDocument() {
    this.AddSubscription(
      this._actionWindowService
        .open(CompanyDocumentFormComponent, this._injector, null, this.company)
        .pipe(switchMap(x => this.documentService.getByOwnerId(this.company.id)))
        .subscribe(res => (this.company.documents = res))
    );
  }

  loadDocuments() {
    if (this.documentsLoaded) {
      return;
    }
    this.AddSubscription(
      this.documentService.getByOwnerId(this.company.id).subscribe(x => {
        this.company.documents = x;
        this.documentsLoaded = true;
      })
    );
  }

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case tenantCommand.deleteDocument:
      case tenantCommand.downloadDocument:
        return command instanceof Command && command.context;
    }
  }

  execute(command: CommandBase): void {
    if (command instanceof Command) {
      switch (command.id) {
        case tenantCommand.deleteDocument:
          this.AddSubscription(
            this.documentService
              .delete(command.context.id)
              .pipe(switchMap(() => this.documentService.getByOwnerId(this.company.id)))
              .subscribe(res => (this.company.documents = res))
          );
          return;
        case tenantCommand.downloadDocument:
          this.AddSubscription(
            this.documentService.download((<Command>command).context).subscribe(res => {
              this._nativeWindow.save(res);
            })
          );
          return;
      }
    }
  }
}
